// ─── Thème (Core) ─────────────────────────────────────────────────
// Version minimale, ALIGNÉE sur l'API de MoPCal (`useUI()` renvoie un objet de
// jetons de couleur ; `makeStyles(UI)` côté écran). On pourra lever les palettes
// complètes de MoPCal plus tard sans changer cette interface.
import React, { createContext, useContext, useMemo, useState, useCallback } from 'react';
import { useColorScheme } from 'react-native';

// Jetons clairs / sombres. Mêmes NOMS que MoPCal (bg, card, ink, muted, accent…)
// pour que le code d'écran levé de MoPCal fonctionne tel quel.
export const UI_LIGHT = {
  bg: '#F6F7FB', card: '#FFFFFF', champ: '#F9FAFB', champBord: '#E5E8F1',
  ink: '#191C28', muted: '#565C70', faint: '#878DA3', border: '#E5E8F1',
  accent: '#6366F1', accentTint: '#EEF0FE', danger: '#D64541', dangerTint: '#FBECEC',
  overlay: 'rgba(20,24,42,0.35)',
  // Surlignage NEUTRE (sélection de colonne/ligne) : ne concurrence aucune couleur
  // d'événement/garde, contrairement à accentTint qui est bleuté.
  selFond: 'rgba(20,24,42,0.06)',
};
export const UI_DARK = {
  bg: '#0D0F15', card: '#161923', champ: '#1B1F2B', champBord: '#272B3A',
  // `faint` remonté (#6B7188 → #8B91A9) : l'ancien passait sous le contraste AA
  // (~3.6:1) sur fond sombre → aides / sous-titres / placeholders illisibles.
  ink: '#ECEEF6', muted: '#A7ACC0', faint: '#8B91A9', border: '#272B3A',
  accent: '#8E93FF', accentTint: '#20233B', danger: '#F0736B', dangerTint: '#2A1616',
  overlay: 'rgba(0,0,0,0.55)',
  selFond: 'rgba(255,255,255,0.06)',
};

const PreferencesUIContext = createContext({ UI: UI_LIGHT, mode: 'light', theme: 'auto', setTheme: () => {} });

export function PreferencesUIProvider({ children, themeParDefaut = 'auto' }) {
  const systeme = useColorScheme();               // 'light' | 'dark' | null
  const [theme, setTheme] = useState(themeParDefaut); // 'auto' | 'light' | 'dark'
  const mode = theme === 'auto' ? (systeme === 'dark' ? 'dark' : 'light') : theme;
  const UI = mode === 'dark' ? UI_DARK : UI_LIGHT;
  const value = useMemo(() => ({ UI, mode, theme, setTheme }), [UI, mode, theme]);
  return <PreferencesUIContext.Provider value={value}>{children}</PreferencesUIContext.Provider>;
}

// API MoPCal-compatible.
export function useUI() { return useContext(PreferencesUIContext).UI; }
export function usePreferencesUI() { return useContext(PreferencesUIContext); }
export function useBasculerTheme() {
  const { theme, setTheme } = useContext(PreferencesUIContext);
  return useCallback(() => setTheme(theme === 'dark' ? 'light' : 'dark'), [theme, setTheme]);
}
